import { useState } from 'react';
import styled from 'styled-components';
import { useGetGeoObjectsQuery, useResetGeoLayoutMutation } from '@shared/api';
import {
  frameRect,
  hasDeveloper,
  rectInside,
  rectsOverlap,
  sheetSize,
  stampRect,
  SHEET_FORMATS,
  ZONE_KEYS,
  zoneRect,
  type GeoSettings,
  type GeoStampField,
  type GeoStampPerson,
  type GeoStampSuggestion,
} from '@entities/geodesy';
import { Button } from '@shared/ui/Button';
import { Input, Select } from '@shared/ui/Input';
import { IconPlus, IconTrash } from '@shared/ui/icons';
import {
  Check,
  ChoiceCard,
  ChoiceGrid,
  DataTable,
  ErrorText,
  FieldGrid,
  FieldLabel,
  FieldNote,
  Fieldset,
  Hint,
  IconButton,
  CellInput,
  Notice,
  Panel,
  PanelHead,
  PanelTitle,
  Stack,
  TableWrap,
  TextArea,
  useAction,
  useDraft,
} from '@widgets/GeoSchemes';
import { StepShell, useSettingsSaver, useStepDirty, useWizard } from './wizard';
import { DraftPreview } from './DraftPreview';
import { StampSuggestions, useStampAssist } from './StampAssist';

/** Подписи штампа ИС САТКОМ, принятых заказчиком, — «стандартные строки». */
const SATKOM_ROLES = ['Операц. Дир.', 'Нач. участка', 'Геодезист'];
/** Подсказки поля «Роль»: штамп САТКОМ и графы 10–13 по ГОСТ Р 21.101. */
const ROLE_OPTIONS = [...SATKOM_ROLES, 'Разработал', 'Проверил', 'Н. контроль', 'Утвердил'];

type StampDraft = GeoSettings['stamp'];
type NotesDraft = Pick<
  GeoSettings,
  | 'basis_drawing'
  | 'extra_notes'
  | 'show_actual_markers'
  | 'show_actual_contour'
  | 'show_point_elevations'
>;

const HeadAction = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing(1)};
  max-width: 100%;

  @container geo-wizard (max-width: 560px) {
    align-items: flex-start;
  }
`;

/** Предложение ИИ-помощника/штампа РД → копия черновика с новым значением графы. */
function withSuggestion(
  stamp: StampDraft,
  notes: NotesDraft,
  s: GeoStampSuggestion,
): { stamp: StampDraft; notes: NotesDraft } {
  const field = s.field;
  if (field === 'basis_drawing') return { stamp, notes: { ...notes, basis_drawing: s.value } };
  const next = { ...stamp };
  next[field] = s.value;
  return { stamp: next, notes };
}

/** Зоны раскладки помещаются в рамку нового формата и не задевают штамп. */
function zonesFit(settings: GeoSettings, sheet: GeoSettings['sheet']): boolean {
  const size = sheetSize(sheet.format, sheet.orientation);
  const frame = frameRect(size);
  const stamp = stampRect(size);
  return ZONE_KEYS.every((k) => {
    const r = zoneRect(settings.layout.zones, k);
    return !r || (rectInside(r, frame) && !rectsOverlap(r, stamp));
  });
}

/** Шаг 4. Черновик по ГОСТ Р 51872: формат листа, основная надпись формы 3, примечания. */
export function StepDraft() {
  const { scheme, readOnly } = useWizard();
  const s = scheme.settings;
  const { data: objects } = useGetGeoObjectsQuery();
  const [resetLayout] = useResetGeoLayoutMutation();
  const relayout = useAction();
  const [relayoutNote, setRelayoutNote] = useState(false);

  const sheet = useDraft(s.sheet);
  const stamp = useDraft(s.stamp);
  const notes = useDraft<NotesDraft>({
    basis_drawing: s.basis_drawing,
    extra_notes: s.extra_notes,
    show_actual_markers: s.show_actual_markers,
    show_actual_contour: s.show_actual_contour ?? true,
    show_point_elevations: s.show_point_elevations ?? false,
  });
  const meta = useDraft({
    object_name: scheme.object_name,
    project_id: scheme.project_id ?? '',
    site_id: scheme.site_id ?? '',
    element_id: scheme.element_id ?? '',
  });
  const dirty = sheet.dirty || stamp.dirty || notes.dirty || meta.dirty;
  useStepDirty('draft', dirty && !readOnly);
  const saver = useSettingsSaver();
  const stampAi = useStampAssist();

  const st = stamp.draft;
  function setPerson(i: number, patch: Partial<GeoStampPerson>) {
    stamp.update((prev) => ({
      ...prev,
      people: prev.people.map((p, idx) => (idx === i ? { ...p, ...patch } : p)),
    }));
  }

  /**
   * Сохранить шаг целиком. `next` — значения, только что записанные в черновик
   * (принятые предложения по листу РД): в этом рендере `draft` их ещё не видит.
   */
  async function save(next?: { stamp: StampDraft; notes: NotesDraft }): Promise<boolean> {
    const stampValue = next?.stamp ?? st;
    const notesValue = next?.notes ?? notes.draft;
    const formatChanged =
      sheet.draft.format !== s.sheet.format || sheet.draft.orientation !== s.sheet.orientation;
    const needRelayout = formatChanged && !zonesFit(s, sheet.draft);
    const ok = await saver.save(
      (cur) => ({
        ...cur,
        sheet: sheet.draft,
        stamp: {
          ...stampValue,
          people: stampValue.people
            .map((p) => ({ role: p.role.trim(), name: p.name.trim(), date: p.date.trim() }))
            .filter((p) => p.role !== '' || p.name !== ''),
        },
        basis_drawing: notesValue.basis_drawing.trim(),
        extra_notes: notesValue.extra_notes.map((n) => n.trim()).filter((n) => n !== ''),
        show_actual_markers: notesValue.show_actual_markers,
        show_actual_contour: notesValue.show_actual_contour ?? true,
        show_point_elevations: notesValue.show_point_elevations ?? false,
      }),
      {
        ...(meta.draft.object_name.trim() !== scheme.object_name
          ? { object_name: meta.draft.object_name.trim() }
          : {}),
        ...(meta.draft.project_id && meta.draft.project_id !== scheme.project_id
          ? { project_id: meta.draft.project_id }
          : {}),
        ...(meta.draft.site_id && meta.draft.site_id !== scheme.site_id
          ? { site_id: meta.draft.site_id }
          : {}),
        ...(meta.draft.element_id !== (scheme.element_id ?? '')
          ? { element_id: meta.draft.element_id || null }
          : {}),
      },
    );
    if (!ok) return false;
    sheet.reset();
    stamp.reset();
    notes.reset();
    meta.reset();
    if (needRelayout) {
      // Области старого формата не влезают в новую рамку — раскладка по умолчанию.
      const done = await relayout.run(
        () => resetLayout(scheme.id).unwrap(),
        'Формат сохранён, но раскладку областей пересчитать не удалось — сбросьте её на шаге 5.',
      );
      setRelayoutNote(done);
    }
    return true;
  }

  /** Принятые человеком предложения → черновик шага → сохранение, как остальные правки шага. */
  function applySuggestions(items: GeoStampSuggestion[]) {
    if (items.length === 0) return;
    let next = { stamp: st, notes: notes.draft };
    for (const it of items) next = withSuggestion(next.stamp, next.notes, it);
    stamp.set(next.stamp);
    notes.set(next.notes);
    void save(next);
  }

  // Пустые обозначение/«Геодезист» не мешают сохранить — это ошибка контроля
  // перед передачей в ПТО, подсказываем заранее.
  const stampIncomplete = st.designation.trim() === '' || !hasDeveloper(st.people);
  const currentStamp: Record<GeoStampField, string> = {
    designation: st.designation,
    object_name: st.object_name,
    structure_name: st.structure_name,
    sheet_title: st.sheet_title,
    stage: st.stage,
    organization: st.organization,
    basis_drawing: notes.draft.basis_drawing,
  };

  return (
    <StepShell
      n={4}
      lead="Оформление по ГОСТ Р 21.101: формат, основная надпись формы 3, примечания. Обозначение и «Разработал» обязательны для передачи в ПТО (ГОСТ Р 51872 п.5.6)."
      save={{
        dirty: dirty && !readOnly,
        busy: saver.busy || relayout.busy,
        error: saver.error,
        onSave: () => save(),
        onReset: () => {
          sheet.reset();
          stamp.reset();
          notes.reset();
          meta.reset();
        },
      }}
    >
      {relayoutNote && (
        <Notice $tone="info" role="status">
          Формат листа изменился — раскладка областей пересчитана по умолчанию. Проверьте её на шаге
          5.
        </Notice>
      )}
      {relayout.error && <ErrorText role="alert">{relayout.error}</ErrorText>}

      <Fieldset disabled={readOnly}>
        <Stack $gap={4}>
          <Panel>
            <PanelHead>
              <PanelTitle>Лист</PanelTitle>
            </PanelHead>
            <Stack $gap={3}>
              <ChoiceGrid $min={96} role="radiogroup" aria-label="Формат листа">
                {SHEET_FORMATS.map((f) => {
                  const size = sheetSize(f, 'portrait');
                  return (
                    <ChoiceCard
                      key={f}
                      name="geo-sheet-format"
                      checked={sheet.draft.format === f}
                      onSelect={() => sheet.set({ ...sheet.draft, format: f })}
                      title={f}
                    >
                      {size.w}×{size.h}
                    </ChoiceCard>
                  );
                })}
              </ChoiceGrid>
              <ChoiceGrid $min={160} role="radiogroup" aria-label="Ориентация листа">
                <ChoiceCard
                  name="geo-sheet-orientation"
                  checked={sheet.draft.orientation === 'landscape'}
                  onSelect={() => sheet.set({ ...sheet.draft, orientation: 'landscape' })}
                  title="Альбомная"
                >
                  длинная сторона горизонтально
                </ChoiceCard>
                <ChoiceCard
                  name="geo-sheet-orientation"
                  checked={sheet.draft.orientation === 'portrait'}
                  onSelect={() => sheet.set({ ...sheet.draft, orientation: 'portrait' })}
                  title="Книжная"
                >
                  длинная сторона вертикально
                </ChoiceCard>
              </ChoiceGrid>
            </Stack>
          </Panel>

          <Panel>
            <PanelHead>
              <PanelTitle>Основная надпись — форма 3</PanelTitle>
              {!readOnly && (
                <HeadAction>
                  <Button
                    type="button"
                    $variant="secondary"
                    $size="sm"
                    onClick={() => void stampAi.run()}
                    disabled={stampAi.busy || !scheme.sheet}
                  >
                    {stampAi.busy ? 'Читаем лист РД…' : 'Заполнить по листу РД'}
                  </Button>
                  {!scheme.sheet && <FieldNote>Нужен проектный лист (шаг 1).</FieldNote>}
                </HeadAction>
              )}
            </PanelHead>
            <Stack $gap={4}>
              {stampAi.error && (
                <Notice $tone="danger" role="alert">
                  {stampAi.error}
                </Notice>
              )}
              {stampAi.result && (
                <StampSuggestions
                  result={stampAi.result}
                  current={currentStamp}
                  busy={saver.busy || relayout.busy}
                  onApply={applySuggestions}
                  onClose={stampAi.clear}
                />
              )}
              <FieldGrid $min={220}>
                <FieldLabel>
                  Обозначение документа (графа 1)
                  <Input
                    value={st.designation}
                    onChange={(e) => stamp.set({ ...st, designation: e.target.value })}
                    placeholder="2026-ДГП-2-ИС-КЖ.1"
                  />
                </FieldLabel>
                <FieldLabel>
                  Объект строительства (графа 2)
                  <Input
                    value={st.object_name}
                    onChange={(e) => stamp.set({ ...st, object_name: e.target.value })}
                  />
                </FieldLabel>
                <FieldLabel>
                  Здание, сооружение (графа 3)
                  <Input
                    value={st.structure_name}
                    onChange={(e) => stamp.set({ ...st, structure_name: e.target.value })}
                  />
                </FieldLabel>
                <FieldLabel>
                  Наименование изображения (графа 5)
                  <Input
                    value={st.sheet_title}
                    onChange={(e) => stamp.set({ ...st, sheet_title: e.target.value })}
                    placeholder="Исполнительная схема свайного поля"
                  />
                  <FieldNote>Масштаб допишется после наименования.</FieldNote>
                </FieldLabel>
                <FieldLabel>
                  Стадия (графа 6)
                  <Input
                    value={st.stage}
                    onChange={(e) => stamp.set({ ...st, stage: e.target.value })}
                    placeholder="Р"
                  />
                </FieldLabel>
                <FieldLabel>
                  Организация (графа 9)
                  <Input
                    value={st.organization}
                    onChange={(e) => stamp.set({ ...st, organization: e.target.value })}
                  />
                </FieldLabel>
              </FieldGrid>

              {stampIncomplete && (
                <Notice $tone="warning" role="status">
                  Для передачи в ПТО нужны обозначение (графа 1) и фамилия в строке «Геодезист»
                  (или «Разработал»).
                </Notice>
              )}
              <Stack $gap={2}>
                <FieldLabel as="div">Подписи (графы 10–13)</FieldLabel>
                {st.people.length > 0 && (
                  <TableWrap>
                    <DataTable>
                      <thead>
                        <tr>
                          <th>Роль</th>
                          <th>Фамилия</th>
                          <th>Дата</th>
                          <th aria-label="Действия" />
                        </tr>
                      </thead>
                      <tbody>
                        {st.people.map((p, i) => (
                          <tr key={i}>
                            <td>
                              <CellInput
                                value={p.role}
                                list="geo-stamp-roles"
                                onChange={(e) => setPerson(i, { role: e.target.value })}
                                aria-label={`Строка ${i + 1}: роль`}
                              />
                            </td>
                            <td>
                              <CellInput
                                value={p.name}
                                onChange={(e) => setPerson(i, { name: e.target.value })}
                                aria-label={`Строка ${i + 1}: фамилия`}
                              />
                            </td>
                            <td>
                              <CellInput
                                value={p.date}
                                onChange={(e) => setPerson(i, { date: e.target.value })}
                                placeholder="10.26"
                                aria-label={`Строка ${i + 1}: дата`}
                              />
                            </td>
                            <td>
                              <IconButton
                                type="button"
                                $danger
                                aria-label={`Удалить строку ${i + 1}`}
                                onClick={() =>
                                  stamp.update((prev) => ({
                                    ...prev,
                                    people: prev.people.filter((_, idx) => idx !== i),
                                  }))
                                }
                              >
                                <IconTrash size={16} />
                              </IconButton>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </DataTable>
                  </TableWrap>
                )}
                <datalist id="geo-stamp-roles">
                  {ROLE_OPTIONS.map((r) => (
                    <option key={r} value={r} />
                  ))}
                </datalist>
                <div>
                  <Button
                    type="button"
                    $variant="secondary"
                    $size="sm"
                    onClick={() =>
                      stamp.update((prev) => ({
                        ...prev,
                        people:
                          prev.people.length === 0
                            ? SATKOM_ROLES.map((role) => ({ role, name: '', date: '' }))
                            : [...prev.people, { role: '', name: '', date: '' }],
                      }))
                    }
                  >
                    <IconPlus size={16} />
                    {st.people.length === 0 ? 'Добавить стандартные строки' : 'Добавить строку'}
                  </Button>
                </div>
              </Stack>
            </Stack>
          </Panel>

          <Panel>
            <PanelHead>
              <PanelTitle>Примечания</PanelTitle>
            </PanelHead>
            <Stack $gap={4}>
              <FieldGrid $min={260}>
                <FieldLabel>
                  Чертёж-основание
                  <Input
                    value={notes.draft.basis_drawing}
                    onChange={(e) => notes.set({ ...notes.draft, basis_drawing: e.target.value })}
                    placeholder="№ 2026-ДГП-2-КЖ.1, лист 3"
                  />
                  <FieldNote>
                    В примечание: «Исполнительная схема выполнена на основании чертежа …» — как
                    написано в поле.
                  </FieldNote>
                </FieldLabel>
              </FieldGrid>
              <FieldLabel>
                Дополнительные примечания — по одному на строку
                <TextArea
                  value={notes.draft.extra_notes.join('\n')}
                  onChange={(e) =>
                    notes.set({ ...notes.draft, extra_notes: e.target.value.split('\n') })
                  }
                  placeholder="Отметки даны в метрах, отклонения — в миллиметрах."
                />
                <FieldNote>
                  СК, система высот, прибор и выписка допусков попадут в примечания сами.
                </FieldNote>
              </FieldLabel>
              <Check
                checked={notes.draft.show_actual_markers}
                onChange={(v) => notes.set({ ...notes.draft, show_actual_markers: v })}
              >
                Показывать на схеме маркеры фактического положения
              </Check>
              <Check
                checked={notes.draft.show_actual_contour ?? true}
                onChange={(v) => notes.set({ ...notes.draft, show_actual_contour: v })}
              >
                Фактический контур конструкции по точкам съёмки (бетон)
              </Check>
              <Check
                checked={notes.draft.show_point_elevations ?? false}
                onChange={(v) => notes.set({ ...notes.draft, show_point_elevations: v })}
              >
                Фактическая отметка у каждой точки съёмки контура
              </Check>
            </Stack>
          </Panel>

          <Panel>
            <PanelHead>
              <PanelTitle>Реквизиты в реестре</PanelTitle>
            </PanelHead>
            <Stack $gap={3}>
              <FieldGrid $min={220}>
                <FieldLabel>
                  Объект в реестре
                  <Input
                    value={meta.draft.object_name}
                    onChange={(e) => meta.set({ ...meta.draft, object_name: e.target.value })}
                  />
                </FieldLabel>
                <FieldLabel>
                  Проект ПТО
                  <Select
                    value={meta.draft.project_id}
                    onChange={(e) => meta.set({ ...meta.draft, project_id: e.target.value })}
                  >
                    {!scheme.project_id && <option value="">— не выбран —</option>}
                    {(objects?.projects ?? []).map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </Select>
                </FieldLabel>
                <FieldLabel>
                  Площадка
                  <Select
                    value={meta.draft.site_id}
                    onChange={(e) => meta.set({ ...meta.draft, site_id: e.target.value })}
                  >
                    {!scheme.site_id && <option value="">— не выбрана —</option>}
                    {(objects?.sites ?? []).map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.name}
                      </option>
                    ))}
                  </Select>
                </FieldLabel>
                <FieldLabel>
                  Конструктивный элемент
                  <Select
                    value={meta.draft.element_id}
                    onChange={(e) => meta.set({ ...meta.draft, element_id: e.target.value })}
                  >
                    <option value="">— не привязан —</option>
                    {(objects?.elements ?? [])
                      .filter(
                        (el) =>
                          (!meta.draft.site_id || el.site_id === meta.draft.site_id) &&
                          (!meta.draft.project_id ||
                            !el.project_id ||
                            el.project_id === meta.draft.project_id),
                      )
                      .map((el) => (
                        <option key={el.id} value={el.id}>
                          {el.code ? `${el.code} — ${el.title}` : el.title}
                        </option>
                      ))}
                  </Select>
                </FieldLabel>
              </FieldGrid>
              <Hint>
                Привязку к проекту и площадке можно сменить, но не снять. Привязка к конструктивному
                элементу автоматически подставляет принятую схему в АОСР и гео-RFI.
              </Hint>
            </Stack>
          </Panel>
        </Stack>
      </Fieldset>

      <DraftPreview beforePreview={dirty ? () => save() : undefined} />
    </StepShell>
  );
}
